import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clearToken, type User } from '../../lib/api';
import { resetIdentity } from '../../lib/analytics';
import { colorForString, initialsFromName } from '../../lib/dashboardHelpers';
import type { PastelColor } from '../c2/Pill';

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};
const pastelDeep: Record<PastelColor, string> = {
  peach: 'var(--color-peach-deep)',
  mint: 'var(--color-mint-deep)',
  sky: 'var(--color-sky-deep)',
  lemon: 'var(--color-lemon-deep)',
  lilac: 'var(--color-lilac-deep)',
};

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      {children}
    </svg>
  );
}

const primaryNav: NavItem[] = [
  {
    to: '/dashboard', end: true, label: 'Links',
    icon: <Icon><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Icon>,
  },
  {
    to: '/dashboard/stats', label: 'Analytics',
    icon: <Icon><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></Icon>,
  },
  {
    to: '/onboarding?fix=extension', label: 'Extension',
    icon: <Icon><path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.864-.95l-.131-1.001c-.094-.71-.846-1.196-1.487-.866-.59.302-1.249.385-1.78.385-1.01 0-1.94-.93-1.94-1.94s.93-1.94 1.94-1.94c.531 0 1.19.083 1.78.385.641.33 1.393-.156 1.487-.866l.131-1.001c.062-.469.394-.88.864-.95a.98.98 0 0 1 .837.276l1.61 1.611Z" /><path d="M11 7.135V5.25C11 4 10 3 8.75 3S6.5 4 6.5 5.25v1.885a.5.5 0 0 1-.5.5h-.385c-.622 0-1.211.288-1.59.78L2.06 11.21a.5.5 0 0 0 0 .58l1.973 2.785c.379.491.969.78 1.59.78H6c.276 0 .5.224.5.5v1.895C6.5 20 7.5 21 8.75 21S11 20 11 18.75v-1.885a.5.5 0 0 1 .5-.5h.385c.622 0 1.212-.288 1.59-.78l1.973-2.79a.5.5 0 0 0 0-.58l-1.973-2.785a2.001 2.001 0 0 0-1.59-.78H11.5a.5.5 0 0 1-.5-.5Z" /></Icon>,
  },
  {
    to: '/dashboard/members', label: 'Members',
    icon: <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>,
  },
];

const accountNav: NavItem[] = [
  {
    to: '/dashboard/settings', label: 'Settings',
    icon: <Icon><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></Icon>,
  },
];

const adminNav: NavItem[] = [
  {
    to: '/admin', end: true, label: 'Admin',
    icon: <Icon><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Icon>,
  },
];

interface SidebarItemProps {
  to: string;
  end?: boolean;
  label: string;
  icon: ReactNode;
}

function SidebarItem({ to, end, label, icon }: SidebarItemProps) {
  const location = useLocation();
  const active = end ? location.pathname === to : location.pathname.startsWith(to);
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 w-full no-underline transition-all duration-150 text-[13px]"
      style={{
        padding: '8px 10px',
        background: active ? 'var(--color-surface)' : 'transparent',
        border: `1px solid ${active ? 'var(--color-hairline)' : 'transparent'}`,
        borderRadius: 8,
        color: active ? 'var(--color-ink)' : 'var(--color-ink-soft)',
        fontWeight: active ? 600 : 500,
        boxShadow: active ? 'var(--shadow-sm)' : 'none',
      }}
    >
      <span
        className="inline-flex shrink-0 w-4 h-4"
        style={{ color: active ? 'var(--accent-solid)' : 'var(--color-ink-muted)' }}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{label}</span>
    </Link>
  );
}

interface SectionProps {
  title: string;
  items: NavItem[];
}

function Section({ title, items }: SectionProps) {
  return (
    <div>
      <div
        className="font-bold uppercase mb-1.5"
        style={{ fontSize: 10.5, color: 'var(--color-ink-muted)', letterSpacing: '0.08em', padding: '0 10px' }}
      >
        {title}
      </div>
      <div className="flex flex-col gap-0.5">
        {items.map(item => (
          <SidebarItem key={item.to} {...item} />
        ))}
      </div>
    </div>
  );
}

interface SidebarProps {
  user: User | null;
}

export default function Sidebar({ user }: SidebarProps) {
  const userName = user?.name ?? user?.email?.split('@')[0] ?? 'You';
  const userInitials = initialsFromName(userName);
  const savedWsName = user?.workspace?.ws_name?.trim() ?? '';
  const emailDomain = user?.email?.split('@')[1] ?? '';
  // If the creator never renamed the workspace, the backend auto-default is just
  // the bare domain (e.g. "acme.com"). Show a friendlier derived name instead.
  const niceFallback = user?.email
    ? (user.email.split('@')[1]?.split('.')[0] ?? 'Workspace').replace(/^./, c => c.toUpperCase())
    : 'Workspace';
  const workspaceLabel = !savedWsName || savedWsName === emailDomain ? niceFallback : savedWsName;
  const workspaceColor = colorForString(workspaceLabel);

  const handleLogout = () => {
    resetIdentity();
    clearToken();
    window.location.href = '/';
  };

  return (
    <aside
      className="flex flex-col sticky top-0"
      style={{
        width: 260,
        flexShrink: 0,
        background: 'var(--color-bg)',
        borderRight: '1px solid var(--color-hairline)',
        height: '100vh',
        padding: '14px 12px 12px',
      }}
    >
      {/* Workspace badge (no switcher — single workspace per user today) */}
      <div
        className="flex items-center gap-2.5 w-full"
        style={{
          padding: '10px',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: 14,
        }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center font-display font-bold text-[15px] shrink-0"
          style={{ background: pastelBg[workspaceColor], color: pastelDeep[workspaceColor] }}
        >
          {workspaceLabel[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-ink leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
            {workspaceLabel}
          </div>
          <div className="text-[11px] text-ink-muted leading-tight mt-0.5">
            {user?.email?.split('@')[1] ?? '—'}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-[18px] flex-1 overflow-y-auto min-h-0 mt-3.5">
        <Section title="Workspace" items={primaryNav} />
        <Section title="Account" items={accountNav} />
        {user?.is_super_admin && <Section title="Super admin" items={adminNav} />}
      </div>

      {/* Profile strip */}
      <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--color-hairline)' }}>
        <div
          className="flex items-center gap-2.5 w-full"
          style={{ padding: '8px 8px', borderRadius: 8 }}
        >
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0"
            style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
          >
            {userInitials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-ink leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
              {userName}
            </div>
            <div className="text-[11px] text-ink-muted leading-tight mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
              {user?.email ?? ''}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-7 h-7 flex items-center justify-center bg-transparent border-0 cursor-pointer shrink-0"
            style={{ borderRadius: 6 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-bg-alt)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            title="Sign out"
            aria-label="Sign out"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-ink-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}
